/**
 * omidLifecycle.js — Reconstruct and validate the OMID viewability lifecycle.
 *
 * Today the proxy only *detects* OMID beacons (omid_session, omid_impression,
 * omid_measurement, omid_video) by URL pattern. Detection alone can't answer the
 * QA question that matters: "did viewability actually measure end-to-end?"
 *
 * The OMID (Open Measurement) session, as emitted by the GMA SDK, follows a
 * lifecycle:
 *     sessionStart  →  impression  →  geometryChange (% in view, repeated)  →  [video quartiles]  →  sessionFinish
 *
 * This module groups OMID beacons into sessions, orders them, and flags broken
 * sequences: impression with no session start, a session that starts but never
 * registers an impression, an impression with no geometry/percent-in-view
 * (so viewability was never actually measured), etc.
 *
 * Pure module — no React/DOM — so it unit-tests in node.
 */

const OMID_TYPES = new Set([
  'omid_session', 'omid_impression', 'omid_measurement', 'omid_video', 'omid_test_beacon',
]);

/** Is this request an OMID-related beacon? */
export function isOmidBeacon(row) {
  return OMID_TYPES.has(row?.requestType) ||
    /type=omid|id=amid|omidsessionstarted|omidimpression/i.test(row?.url || '');
}

function qs(url = '') {
  const out = {};
  try {
    const i = url.indexOf('?');
    if (i < 0) return out;
    for (const pair of url.slice(i + 1).split('&')) {
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      let k, v;
      try { k = decodeURIComponent(pair.slice(0, eq)); } catch { k = pair.slice(0, eq); }
      try { v = decodeURIComponent(pair.slice(eq + 1)); } catch { v = pair.slice(eq + 1); }
      out[k] = v;
    }
  } catch {}
  return out;
}

function ts(row) {
  // `startedAt` last: it IS in LIFECYCLE_ROW_FIELDS (live rows carry it), so an
  // OMID beacon that only has startedAt still orders (slot-story WP1, AC 1.17).
  const t = row?.ts ?? row?.timestamp ?? row?.time ?? row?.startedAt;
  if (typeof t === 'number') return t;
  const n = Date.parse(t);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Classify a single OMID beacon into a lifecycle phase and pull useful fields.
 * GMA OMID payloads vary; we read the common signals defensively.
 */
export function classifyOmidEvent(row) {
  const url = row?.url || '';
  const p = qs(url);
  const lc = url.toLowerCase();

  // session id: many possible carriers
  const sessionId =
    p.adsid || p.sid || p.adSessionId || p.asi || p.session ||
    row?.validData?.adSessionId || null;

  // percent-in-view signals (geometryChange). GMA ActiveView uses tos/mtos;
  // OMID geometry carries a percentageInView. We look for any of them.
  const percent = firstNumber([
    p.percentageInView, p.piv, p.perc, p.p, p.pv, p.avgpv,
  ]);
  const tos = firstNumber([p.tos, p.mtos]);

  let phase = 'measurement';
  if (/omidsessionstarted|type=omidstart|sessionstart/i.test(lc) || row.requestType === 'omid_session') phase = 'sessionStart';
  else if (/omidimpression|type=omidimpression/i.test(lc) || row.requestType === 'omid_impression') phase = 'impression';
  else if (/omidvideo|type=omidvideo/i.test(lc) || row.requestType === 'omid_video') phase = 'video';
  else if (/sessionfinish|omidfinish/i.test(lc)) phase = 'sessionFinish';
  else if (percent != null || tos != null || /geometry|id=amid/i.test(lc)) phase = 'geometryChange';

  return {
    phase,
    sessionId,
    percent,
    tos,
    iu: p.iu || row?.validData?.iu || null,
    // The GMA SDK sends its correlator as `c`; GPT sends `correlator`. Read both
    // (the impression matcher in buildSlotLifecycles already reads `c` first).
    correlator: p.correlator || p.c || row?.validData?.correlator || null,
    vendor: p.omidpn || p.vendor || row?.validData?.omidpn || null,
    ts: ts(row),
    url,
    // The row this beacon came from, so a session can point at its evidence.
    rowKey: row?._k ?? row?.id ?? null,
  };
}

function firstNumber(arr) {
  for (const v of arr) {
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Group OMID beacons into sessions and validate each lifecycle.
 * Grouping key: adSessionId when present, else correlator, else iu.
 *
 * @param {Array} rows  — all request rows (any types; non-OMID ignored)
 * @returns {Array} sessions: { key, vendor, iu, events[], phases:Set, issues[], status, percentInViewMax }
 */
export function reconstructOmidSessions(rows = []) {
  const events = rows.filter(isOmidBeacon).map(classifyOmidEvent);
  const groups = new Map();

  for (const e of events) {
    // Grouping key: adSessionId when present, else correlator, else iu. A GMA
    // correlator is shared by EVERY slot request in an app session, so a
    // correlator-keyed group also carries `iu` as a slot discriminator: two
    // slots that share a correlator but differ in iu are separate sessions.
    const key = e.sessionId
      || (e.correlator ? (e.iu ? `${e.correlator}|${e.iu}` : e.correlator) : null)
      || e.iu
      || '∅';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }

  const sessions = [];
  for (const [key, evs] of groups.entries()) {
    evs.sort((a, b) => a.ts - b.ts);
    const phases = new Set(evs.map(e => e.phase));
    const percents = evs.map(e => e.percent).filter(n => n != null);
    const percentInViewMax = percents.length ? Math.max(...percents) : null;

    const issues = [];
    const hasStart = phases.has('sessionStart');
    const hasImpression = phases.has('impression');
    const hasGeometry = phases.has('geometryChange') || percents.length > 0;
    const keyOf = (pred) => (evs.find(pred)?.rowKey) ?? null;

    // Stable codes + phase:'viewable' + rowKey (the beacon the finding came from)
    // follow Contract A (docs/slot-story-spec.md). `severity`/`message` stay as the
    // Lifecycle tab reads them; `level`/`msg` mirror them for slot-issue consumers.
    const pushIssue = (code, severity, message, rowKey) => {
      issues.push({ severity, message, level: severity, msg: message, phase: 'viewable', code, rowKey });
    };

    if (hasImpression && !hasStart) {
      pushIssue('omid_session_without_start', 'warn', 'OMID impression fired without a session start beacon', keyOf(e => e.phase === 'impression'));
    }
    if (hasStart && !hasImpression) {
      pushIssue('omid_no_impression', 'fail', 'OMID session started but no impression registered — viewability incomplete', keyOf(e => e.phase === 'sessionStart'));
    }
    if (hasImpression && !hasGeometry) {
      pushIssue('omid_no_geometry', 'fail', 'OMID impression with no geometry/percent-in-view — viewability never measured', keyOf(e => e.phase === 'impression'));
    }
    if (hasGeometry && percentInViewMax === 0) {
      pushIssue('omid_zero_pct', 'warn', 'Geometry reported 0% in view — ad never visible', keyOf(e => e.percent === 0));
    }
    // ordering sanity: impression should not precede session start
    if (hasStart && hasImpression) {
      const startTs = evs.find(e => e.phase === 'sessionStart')?.ts ?? 0;
      const impTs = evs.find(e => e.phase === 'impression')?.ts ?? 0;
      if (impTs && startTs && impTs < startTs) {
        pushIssue('omid_out_of_order', 'warn', 'OMID impression beacon precedes session start (out-of-order)', keyOf(e => e.phase === 'impression'));
      }
    }

    const status = issues.some(i => i.severity === 'fail') ? 'fail'
                 : issues.length ? 'warn'
                 : 'pass';

    sessions.push({
      key,
      vendor: evs.find(e => e.vendor)?.vendor || null,
      iu: evs.find(e => e.iu)?.iu || null,
      correlator: evs.find(e => e.correlator)?.correlator || null,
      events: evs,
      phases,
      eventCount: evs.length,
      percentInViewMax,
      issues,
      status,
      // The '∅' bucket is every beacon with no session id, correlator or iu —
      // across slots. It proves nothing about any one slot and never attaches.
      attachable: key !== '∅',
    });
  }

  return sessions;
}

/**
 * Validate a single ad slot's OMID coverage given its associated rows.
 * Returns a validators-compatible result group, or null if no OMID activity.
 */
export function omidLifecycleGroup(rows = []) {
  const sessions = reconstructOmidSessions(rows);
  if (!sessions.length) return null;

  const results = sessions.map(s => ({
    id: `omid_${s.key}`,
    label: `OMID session ${s.iu || s.key}`,
    severity: s.status === 'fail' ? 'fail' : s.status === 'warn' ? 'warn' : 'pass',
    message: s.issues.length
      ? s.issues.map(i => i.message).join('; ')
      : `Viewability measured end-to-end${s.percentInViewMax != null ? ` (max ${s.percentInViewMax}% in view)` : ''}`,
    _omid: true,
    _phases: [...s.phases],
  }));

  return { title: 'OMID Viewability Lifecycle', scope: 'OMID', results, _omid: true };
}
